/**
 * NEW_UX editor control primitives: radio-cards, channel chips, block picker, toggle,
 * status chip, labels, merge-tag pill. Presentational only.
 */
import styled from '@emotion/styled';
import {
  ArrowDownWideNarrow,
  ArrowUpNarrowWide,
  ListFilter,
  MoreVertical,
  Plus,
  Search,
  X
} from 'lucide-react';
import React from 'react';

import { LightMdToJsx } from '../../../../../../services/formatter';
import { ring, ringActive, tokens } from './tokens';

const { color, radius, font } = tokens;

/* --------------------------------------------------------------------- labels */

/** Group label above a set of controls (uppercase h7, grey500). */
export const SectionLabel = styled('div')`
  font-size: ${font.variant.h7.fontSize}px;
  line-height: ${font.variant.h7.lineHeight}px;
  font-weight: ${font.variant.h7.fontWeight};
  text-transform: ${font.variant.h7.textTransform};
  color: ${color.grey500};
  margin: ${tokens.space(4)} 0 ${tokens.space(1.5)};
`;

/** Field label (above an input) — body heading, black. */
export const FieldLabel = styled('div')`
  font-size: ${font.variant.h6.fontSize}px;
  line-height: ${font.variant.h6.lineHeight}px;
  font-weight: ${font.variant.h6.fontWeight};
  color: ${color.black};
  margin-bottom: ${tokens.space(1)};
`;

/** Secondary/help text under a field — caption, grey500. */
export const FieldHint = styled('div')`
  font-size: ${font.variant.caption.fontSize}px;
  line-height: ${font.variant.caption.lineHeight}px;
  font-weight: ${font.variant.caption.fontWeight};
  color: ${color.grey500};
  margin-top: ${tokens.space(1)};
`;

/** Emphasis within a timing-summary sentence — weight 400, not browser-default
 * <strong> bold. */
const TimingEmphasis = styled('span')`
  font-weight: 400;
`;
const timingEmphasisFormatter = {
  '**': (t: string) => <TimingEmphasis>{t}</TimingEmphasis>
};

/** Renders a timing-summary sentence with `**...**` markdown emphasis at weight
 * 400 (not the browser-default bold `LightMdToJsx` would otherwise apply). Used
 * for the Task/Message/Automation "Starts" summary text. */
export const TimingSummaryText: React.FC<{ text: string }> = ({ text }) => (
  <LightMdToJsx formatter={timingEmphasisFormatter}>{text}</LightMdToJsx>
);

/* ---------------------------------------------------------------- radio cards */

// A plain <div role="button"> rather than a native <button> — a selected card
// renders real interactive children (text/time inputs, buttons), and nesting
// those inside a <button> is invalid HTML: the browser "fixes up" the DOM,
// which desyncs from React's tree and can silently steal focus from the input
// on every keystroke.
const RadioCardBox = styled('div')<{ selected?: boolean; disabled?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  border: 0;
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  font-family: ebony, sans-serif;
  border-radius: ${radius.md}px;
  padding: ${tokens.space(3)};
  margin-bottom: ${tokens.space(2)};
  background: ${(p) => (p.disabled ? color.grey100 : p.selected ? color.blue100 : color.white)};
  box-shadow: ${(p) => (p.disabled ? ring() : p.selected ? ringActive : ring())};
  &:hover {
    box-shadow: ${(p) =>
      p.disabled ? ring() : p.selected ? ringActive : ring(color.n300)};
  }
`;

const RadioRow = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: ${tokens.space(2)};
`;

const RadioDot = styled('span')<{ selected?: boolean; disabled?: boolean }>`
  flex: 0 0 auto;
  margin-top: 2px;
  width: 16px;
  height: 16px;
  border-radius: ${radius.pill}px;
  box-shadow: ${(p) =>
    p.disabled
      ? `inset 0 0 0 1.5px ${color.grey700}`
      : p.selected
      ? `inset 0 0 0 5px ${color.blue600}`
      : `inset 0 0 0 1.5px ${color.n300}`};
`;

const RadioTitle = styled('div')<{ selected?: boolean; disabled?: boolean }>`
  font-size: ${font.variant.body1.fontSize}px;
  font-weight: ${font.weightScale.medium};
  color: ${(p) => (p.disabled ? color.grey700 : p.selected ? color.blue600 : color.black)};
`;

const RadioDesc = styled('div')`
  font-size: ${font.variant.caption.fontSize}px;
  line-height: ${font.variant.caption.lineHeight}px;
  font-weight: ${font.variant.caption.fontWeight};
  color: ${color.grey500};
  margin-top: 2px;
`;

/** Selectable option card with a radio dot, title, description, and optional body
 * (revealed inline when selected — e.g. the "At a fixed time → Send N days before" row). */
export const RadioCard: React.FC<{
  selected?: boolean;
  disabled?: boolean;
  title: React.ReactNode;
  description?: React.ReactNode;
  onSelect: () => void;
  children?: React.ReactNode;
}> = ({ selected, disabled, title, description, onSelect, children }) => (
  <RadioCardBox
    selected={selected}
    disabled={disabled}
    onClick={disabled ? undefined : onSelect}
    role="button"
    aria-pressed={selected}
    aria-disabled={disabled}
    tabIndex={disabled ? -1 : 0}
    onKeyDown={
      disabled
        ? undefined
        : (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect();
            }
          }
    }
  >
    <RadioRow>
      <RadioDot selected={selected} disabled={disabled} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <RadioTitle selected={selected} disabled={disabled}>{title}</RadioTitle>
        {description && <RadioDesc>{description}</RadioDesc>}
        {selected && children ? (
          <div style={{ marginTop: 10 }}>{children}</div>
        ) : null}
      </div>
    </RadioRow>
  </RadioCardBox>
);

/* -------------------------------------------------------------- channel chips */

const ChannelChipBox = styled('button')<{ selected?: boolean; disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${tokens.space(2)};
  border: 0;
  border-radius: ${radius.md}px;
  padding: ${tokens.space(2)} ${tokens.space(3)};
  font-family: ebony, sans-serif;
  font-size: ${font.variant.caption.fontSize}px;
  font-weight: ${font.variant.caption.fontWeight};
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.disabled ? 0.5 : 1)};
  background: ${(p) => (p.selected ? color.blue100 : color.white)};
  color: ${(p) => (p.selected ? color.blue600 : color.black)};
  box-shadow: ${(p) => (p.selected ? ringActive : ring())};
  & svg {
    width: 16px;
    height: 16px;
  }
`;

export const ChannelChip: React.FC<{
  selected?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  label: React.ReactNode;
  onToggle: () => void;
}> = ({ selected, disabled, icon, label, onToggle }) => (
  <ChannelChipBox
    type="button"
    selected={selected}
    disabled={disabled}
    onClick={() => !disabled && onToggle()}
  >
    {icon}
    {label}
  </ChannelChipBox>
);

export const ChipRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: ${tokens.space(2)};
`;

/* -------------------------------------------------------------- block picker */

export const BlockPickerGrid = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${tokens.space(2)};
`;

const BlockBtn = styled('button')<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${tokens.space(2)};
  text-align: left;
  border: 0;
  cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};
  background: ${color.white};
  border-radius: ${radius.md}px;
  padding: ${tokens.space(2.5)} ${tokens.space(3)};
  box-shadow: ${ring()};
  font-family: ebony, sans-serif;
  font-size: ${font.variant.caption.fontSize}px;
  font-weight: ${font.variant.caption.fontWeight};
  color: ${color.grey700};
  opacity: ${(p) => (p.disabled ? 0.5 : 1)};
  & svg {
    width: 16px;
    height: 16px;
    color: ${color.grey700};
    flex: 0 0 auto;
  }
  &:hover {
    background: ${(p) => (p.disabled ? color.white : color.n100)};
    color: ${(p) => (p.disabled ? color.grey700 : color.black)};
  }
`;

export const BlockButton: React.FC<{
  icon?: React.ReactNode;
  label: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}> = ({ icon, label, onClick, disabled }) => (
  <BlockBtn type="button" disabled={disabled} onClick={disabled ? undefined : onClick}>
    {icon}
    {label}
  </BlockBtn>
);

/* Filter pills (all / content / subtasks) */
export const FilterPillRow = styled('div')`
  display: flex;
  gap: ${tokens.space(1)};
  margin-bottom: ${tokens.space(3)};
`;

const FilterPillBtn = styled('button')<{ active?: boolean }>`
  border: 0;
  cursor: pointer;
  border-radius: ${radius.pill}px;
  padding: ${tokens.space(1)} ${tokens.space(3)};
  font-size: ${font.size.hint}px;
  font-weight: ${font.weight.label};
  background: ${(p) => (p.active ? color.n900 : 'transparent')};
  color: ${(p) => (p.active ? color.white : color.n600)};
  box-shadow: ${(p) => (p.active ? 'none' : ring())};
`;

export const FilterPill: React.FC<{
  active?: boolean;
  label: string;
  onClick: () => void;
}> = ({ active, label, onClick }) => (
  <FilterPillBtn type="button" active={active} onClick={onClick}>
    {label}
  </FilterPillBtn>
);

/* ------------------------------------------------------- segmented toggle group */

export const SegmentedToggleGroup = styled('div')`
  display: inline-flex;
  border-radius: ${radius.md}px;
  box-shadow: ${ring()};
  overflow: hidden;
`;

const SegmentedToggleButtonBox = styled('button')<{ active?: boolean }>`
  border: 0;
  cursor: pointer;
  padding: ${tokens.space(1)} ${tokens.space(3)};
  font-family: ebony, sans-serif;
  font-size: ${font.variant.caption.fontSize}px;
  font-weight: ${font.weightScale.regular};
  color: ${(p) => (p.active ? color.blue600 : color.n600)};
  /* Simulates a bolder look on the active segment without changing font-weight —
     an actual weight change would reflow the text and visibly shift neighboring
     segments. */
  text-shadow: ${(p) => (p.active ? '0 0 0.4px currentColor' : 'none')};
  background: ${(p) => (p.active ? color.blue100 : 'transparent')};
  &:not(:last-child) {
    border-right: 1px solid ${color.borderSubtle};
  }
`;

export const SegmentedToggleButton: React.FC<{
  active?: boolean;
  label: string;
  onClick: () => void;
}> = ({ active, label, onClick }) => (
  <SegmentedToggleButtonBox type="button" active={active} onClick={onClick}>
    {label}
  </SegmentedToggleButtonBox>
);

/* --------------------------------------------------------------------- search */

const SearchInputBox = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: ${ring()};
  border-radius: ${radius.sm}px;
  padding: 6px ${tokens.space(2)};
  & svg {
    flex: 0 0 auto;
    width: 14px;
    height: 14px;
    color: ${color.n500};
  }
  & input {
    border: 0;
    outline: none;
    background: none;
    font-family: ebony, sans-serif;
    font-size: ${font.variant.caption.fontSize}px;
    font-weight: ${font.weightScale.regular};
    width: 200px;
  }
`;

export const SearchInput: React.FC<{
  value: string;
  onChange: (nextValue: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
}> = ({ value, onChange, placeholder, autoFocus, className }) => (
  <SearchInputBox className={className}>
    <Search />
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
    />
  </SearchInputBox>
);

/* --------------------------------------------------------------------- filter/sort buttons */

const FilterButtonBox = styled('button')<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  border-radius: ${radius.sm}px;
  cursor: pointer;
  background: ${(p) => (p.active ? color.blue100 : 'none')};
  box-shadow: ${ring()};
  padding: 6px ${tokens.space(2)};
  font-family: ebony, sans-serif;
  font-size: ${font.variant.caption.fontSize}px;
  font-weight: ${font.weightScale.regular};
  color: ${(p) => (p.active ? color.blue600 : color.n700)};
  white-space: nowrap;
  & svg {
    flex: 0 0 auto;
    width: 14px;
    height: 14px;
    color: ${(p) => (p.active ? color.blue600 : color.n500)};
  }
  &:hover {
    box-shadow: ${ring(color.n300)};
  }
`;

export const FilterButton: React.FC<{
  label: string;
  active?: boolean;
  onClick: () => void;
  className?: string;
}> = ({ label, active, onClick, className }) => (
  <FilterButtonBox type="button" active={active} onClick={onClick} className={className}>
    <ListFilter />
    {label}
  </FilterButtonBox>
);

export const SortButton: React.FC<{
  descending: boolean;
  onClick: () => void;
  label: string;
  className?: string;
}> = ({ descending, onClick, label, className }) => (
  <FilterButtonBox type="button" onClick={onClick} className={className}>
    {descending ? <ArrowDownWideNarrow /> : <ArrowUpNarrowWide />}
    {label}
  </FilterButtonBox>
);

const TextLinkButtonBox = styled('button')`
  border: 0;
  background: none;
  cursor: pointer;
  padding: 0;
  font-family: ebony, sans-serif;
  font-size: ${font.variant.caption.fontSize}px;
  font-weight: ${font.weightScale.regular};
  color: ${color.blue600};
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
  }
`;

export const TextLinkButton: React.FC<{
  label: string;
  onClick: () => void;
  className?: string;
}> = ({ label, onClick, className }) => (
  <TextLinkButtonBox type="button" onClick={onClick} className={className}>
    {label}
  </TextLinkButtonBox>
);

/* --------------------------------------------------------------------- ring icon button */

/**
 * Small ring-bordered icon+label button — "Supporting actor"/"Email address" (Send a copy
 * to), "Copy saved segment", "Create new segment" all rendered the same hand-rolled inline
 * style independently before this was extracted into one shared component.
 */
export const RingIconButton = styled('button')<{ selected?: boolean; height?: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${tokens.space(2)};
  flex: 1;
  height: ${(p) => p.height ?? 36}px;
  padding: 0 ${tokens.space(3)};
  border-radius: ${radius.md}px;
  border: 0;
  cursor: pointer;
  font-family: inherit;
  font-size: ${font.variant.h7.fontSize}px;
  font-weight: ${font.weightScale.regular};
  box-shadow: ${(p) => (p.selected ? ringActive : ring())};
  background: ${(p) => (p.selected ? color.blue100 : color.white)};
  color: ${(p) => (p.selected ? color.blue600 : color.black)};
  & svg {
    color: ${(p) => (p.selected ? color.blue600 : color.black)};
  }
`;

/* --------------------------------------------------------------------- toggle */

const ToggleTrack = styled('button')<{ on?: boolean }>`
  width: 36px;
  height: 20px;
  flex: 0 0 auto;
  border: 0;
  cursor: pointer;
  border-radius: ${radius.pill}px;
  background: ${(p) => (p.on ? color.blue600 : color.n300)};
  position: relative;
  transition: background 0.15s ease;
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${(p) => (p.on ? '18px' : '2px')};
    width: 16px;
    height: 16px;
    border-radius: ${radius.pill}px;
    background: ${color.white};
    transition: left 0.15s ease;
  }
`;

export const Toggle: React.FC<{ on?: boolean; onChange: (on: boolean) => void; ariaLabel?: string }>
  = ({ on, onChange, ariaLabel }) => (
    <ToggleTrack type="button" on={on} aria-label={ariaLabel} aria-pressed={!!on} onClick={() => onChange(!on)} />
  );

/* ---------------------------------------------------------------- status chip */

export type StatusChipTone = 'grey' | 'red' | 'blue' | 'green' | 'yellow';
export type StatusChipVariant = 'light' | 'dark';

/**
 * Colors are read from the classic emotion theme (p.theme.custom.colors), not the kit's
 * own token scale — each state is specced directly against the classic named palette
 * (e.g. red200/red400), so this stays pinned to that source rather than approximating it
 * through the kit's separate (and not equivalent) neutrals.
 */
const StatusChipBox = styled('span')<{
  tone: StatusChipTone;
  variant: StatusChipVariant;
}>`
  display: inline-flex;
  align-items: center;
  border-radius: ${radius.pill}px;
  padding: 2px ${tokens.space(2)};
  font-size: ${font.variant.caption.fontSize}px;
  white-space: nowrap;
  ${(p) => {
    const c = p.theme.custom.colors;
    const styles: Record<
      StatusChipTone,
      Partial<Record<StatusChipVariant, { bg: string; fg: string; weight: number }>>
    > = {
      grey: {
        light: { bg: c.gray.w200, fg: c.gray.w700, weight: 200 },
        dark: { bg: c.gray.w400, fg: c.gray.w900, weight: 200 }
      },
      red: {
        light: { bg: c.red.w200, fg: c.red.w700, weight: 200 },
        dark: { bg: c.red.w400, fg: c.gray.white, weight: 400 }
      },
      blue: {
        light: { bg: c.blue.w200, fg: c.blue.w600, weight: 200 }
      },
      green: {
        light: { bg: c.green.w200, fg: c.green.w600, weight: 200 }
      },
      yellow: {
        light: { bg: c.yellow.w200, fg: c.yellow.w800, weight: 200 }
      }
    };
    const s = styles[p.tone][p.variant] || styles[p.tone].light;
    return `background: ${s?.bg}; color: ${s?.fg}; font-weight: ${s?.weight};`;
  }}
`;

export const StatusChip: React.FC<{
  tone: StatusChipTone;
  variant?: StatusChipVariant;
  children: React.ReactNode;
}> = ({ tone, variant = 'light', children }) => (
  <StatusChipBox tone={tone} variant={variant}>
    {children}
  </StatusChipBox>
);

/* ---------------------------------------------------------------- actor chip */

/**
 * Background is always the actor's own assigned color; text/icon color is fixed
 * (classic theme's `colors.gray.black`, #08121a) regardless of the background.
 */
const ActorChipBox = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: ebony, sans-serif;
  font-size: ${font.variant.caption.fontSize}px;
  font-weight: ${font.weightScale.regular};
  padding: 2px ${tokens.space(2)};
  border-radius: ${radius.pill}px;
  white-space: nowrap;
  color: ${(p) => p.theme.custom.colors.gray.black};
  & svg {
    width: 16px;
    height: 16px;
    stroke-width: 1;
  }
`;

export const ActorChip: React.FC<{
  color: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  /** True for a color from the old actor palette, still saved on templates from before
   * the v4 palette — rendered tinted (diminished opacity) rather than full-strength, so
   * old and new colors stay visually distinguishable side by side. */
  dimmed?: boolean;
  /** Renders a trailing X button (e.g. "remove this recipient") when provided. */
  onRemove?: () => void;
  removeAriaLabel?: string;
}> = ({ color: bgColor, icon, children, dimmed, onRemove, removeAriaLabel }) => (
  <ActorChipBox style={{ background: dimmed ? `${bgColor}22` : bgColor }}>
    {icon}
    {children}
    {onRemove && (
      <button
        type="button"
        aria-label={removeAriaLabel}
        onClick={onRemove}
        style={{
          border: 0,
          background: 'transparent',
          cursor: 'pointer',
          display: 'flex',
          padding: 0,
          color: 'inherit'
        }}
      >
        <X size={14} />
      </button>
    )}
  </ActorChipBox>
);

/* --------------------------------------------------------------- active pill */

/**
 * Active / Deactivated indicator. Uses the shared actor-chip style (pill, black
 * text on a light fill, no status dot) so it matches the chips used across NEW_UX
 * rather than being its own bespoke pill — light green when active, light grey when
 * deactivated. Driven by the item's `deactivated` flag: the editor shells pass
 * `active={!value.deactivated}` and expose Deactivate/Reactivate in the top-bar
 * overflow menu.
 */
export const ActivePill: React.FC<{ active?: boolean }> = ({ active = true }) => (
  <ActorChipBox style={{ background: active ? color.green050 : color.n200 }}>
    {active ? 'Active' : 'Deactivated'}
  </ActorChipBox>
);

/* ------------------------------------------------------------- overflow menu */

const OverflowBtn = styled('button')`
  width: 34px;
  height: 34px;
  border: 0;
  background: transparent;
  border-radius: ${radius.md}px;
  color: ${color.n600};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: ${color.n100};
  }
`;

const OverflowMenuBox = styled('div')`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  min-width: 160px;
  background: ${color.white};
  border-radius: ${radius.md}px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.06);
  padding: 6px;
  z-index: 40;
`;

const OverflowItem = styled('button')<{ danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 32px;
  padding: 0 8px;
  border: 0;
  background: transparent;
  border-radius: ${radius.sm}px;
  cursor: pointer;
  font-family: ebony, sans-serif;
  font-size: ${font.variant.body1.fontSize}px;
  font-weight: ${font.variant.body1.fontWeight};
  color: ${(p) => (p.danger ? color.red500 : color.n800)};
  text-align: left;
  & svg {
    width: 15px;
    height: 15px;
    stroke-width: 1.5px;
  }
  &:hover {
    background: ${(p) => (p.danger ? '#FEF2F2' : color.n100)};
  }
`;

export type OverflowMenuItem = {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
};

/** Top-bar overflow (⋮) menu — e.g. Duplicate / Delete. */
export const OverflowMenu: React.FC<{ items: OverflowMenuItem[] }> = ({
  items
}) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  if (!items.length) return null;
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <OverflowBtn
        type="button"
        aria-label="More options"
        onClick={() => setOpen((o) => !o)}
      >
        <MoreVertical size={18} />
      </OverflowBtn>
      {open && (
        <OverflowMenuBox role="menu">
          {items.map((it, i) => (
            <OverflowItem
              key={i}
              type="button"
              danger={it.danger}
              onClick={() => {
                setOpen(false);
                it.onClick();
              }}
            >
              {it.icon}
              {it.label}
            </OverflowItem>
          ))}
        </OverflowMenuBox>
      )}
    </div>
  );
};

/* ----------------------------------------------------------- audience tabs */

/**
 * Audience/segment tabs shown on top of the preview sheet (prototype SegmentChips):
 * "All — send to everyone" (green) + one tab per segment (its color) + optional "+ Add
 * segment". Clicking selects which audience the preview is filtered to (null = everyone).
 */
/**
 * Diagonal "slash" overlay for an empty segment's color swatch/chip — signals
 * "nothing will be sent to this segment". `stroke` is the slash color: white over a
 * solid swatch, or the segment's own color over a light tint. Mirrors the prototype
 * (proto-app.jsx SegmentChips / editor-panels.jsx AudiencePanel).
 */
export const segmentEmptySlash = (stroke = 'rgba(255,255,255,.95)') =>
  `linear-gradient(135deg, transparent calc(50% - 1px), ${stroke} calc(50% - 1px), ${stroke} calc(50% + 1px), transparent calc(50% + 1px))`;

export const AudienceTabs: React.FC<{
  /**
   * Lead tab — the unfiltered "everything" preview. Always first, always shown.
   * Its label AND colour depend on whether segments exist: with NO segments it IS
   * the everyone audience, so it reads `allLabelEveryone` in brand green; with 2+
   * audiences it is the literal every-block union (not the everyone-else catch-all),
   * so it reads `allLabelBlocks` in neutral grey.
   */
  allLabelEveryone?: string;
  allLabelBlocks?: string;
  /**
   * The EVERYONE catch-all audience, rendered as the LAST tab and only once segments
   * exist (with no segments the single "All audiences" tab already is everyone).
   * Selecting it previews the everyone-else-filtered view, like any segment.
   */
  everyone?: { id: string; label?: string; empty?: boolean };
  segments: { id: string; label: string; color: string; empty?: boolean }[];
  activeId: string | null;
  onSelect: (id: string | null) => void;
  onAdd?: () => void;
  onDelete?: (id: string) => void;
}> = ({
  allLabelEveryone = 'All – sends to everyone',
  allLabelBlocks = 'All blocks',
  everyone,
  segments,
  activeId,
  onSelect,
  onAdd,
  onDelete
}) => {
  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '4px 10px 5px',
    fontFamily: 'ebony, sans-serif',
    fontSize: font.size.badge,
    fontWeight: font.weight.title,
    lineHeight: 1.3,
    borderRadius: '7px 7px 0 0',
    cursor: 'pointer',
    border: 0,
    whiteSpace: 'nowrap',
    flex: 'none'
  };
  // Readable text (near-black vs white) for a solid tab fill, from the fill's
  // perceived luminance.
  const textOn = (bg: string) => {
    const m = /^#?([0-9a-f]{6})$/i.exec(bg.trim());
    if (!m) return color.white;
    const v = parseInt(m[1], 16);
    const lum =
      (0.299 * ((v >> 16) & 255) +
        0.587 * ((v >> 8) & 255) +
        0.114 * (v & 255)) /
      255;
    return lum > 0.62 ? color.n900 : color.white;
  };
  // Selected → outline ring in the tab's own colour, with a white gap so it stays
  // legible against the fill; unselected → a soft lift shadow only.
  const tabShadow = (active: boolean, ringColor: string) =>
    active
      ? `0 -1px 4px rgba(0,0,0,.10), 0 0 0 1.5px ${color.white}, 0 0 0 3px ${ringColor}`
      : '0 -1px 3px rgba(0,0,0,.06)';
  // The lead tab is ALWAYS "All audiences" — the unfiltered, every-block preview. It
  // uses the brand green (mirroring the preview sheet's top bar). The EVERYONE
  // catch-all is a distinct trailing tab (black, like its swatch in the left column),
  // shown only once segments exist.
  const hasSegments = segments.length > 0;
  // Once a 3rd custom segment is assigned, segment + Everyone Else tabs collapse to
  // color-only mini squares so the row doesn't overflow/wrap (matches the prototype's
  // orderedAssigned.length >= 4 threshold: 3 segments + the Everyone Else tab).
  const condensed = segments.length >= 3;
  const miniBase: React.CSSProperties = {
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 26,
    height: 26,
    borderRadius: '6px 6px 0 0',
    cursor: 'pointer',
    border: 0,
    flex: 'none'
  };
  const everyoneLabel = everyone?.label || 'Everyone else';
  // No segments → the lead tab literally IS the everyone audience (green). With 2+
  // audiences it is the every-block union — distinct from the everyone-else catch-all
  // tab — so it drops to neutral grey and reads "All blocks".
  const leadColor = hasSegments ? color.grey500 : color.green;
  const leadLabel = hasSegments ? allLabelBlocks : allLabelEveryone;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 4,
        flexWrap: 'wrap',
        paddingLeft: 8,
        marginBottom: 12
      }}
    >
      <button
        type="button"
        onClick={() => onSelect(null)}
        title={hasSegments ? 'Preview every block, unfiltered' : undefined}
        style={{
          ...base,
          background: leadColor,
          color: textOn(leadColor),
          boxShadow: tabShadow(activeId === null, leadColor)
        }}
      >
        {leadLabel}
      </button>
      {segments.map((s) => {
        const fg = textOn(s.color);
        const slashStroke =
          fg === color.white ? 'rgba(255,255,255,.9)' : 'rgba(0,0,0,.45)';
        const tooltip = s.empty
          ? `${s.label} — nothing will be sent to this segment`
          : s.label;
        if (condensed) {
          return (
            <span
              key={s.id}
              role="button"
              onClick={() => onSelect(s.id)}
              title={tooltip}
              style={{
                ...miniBase,
                background: s.color,
                backgroundImage: s.empty
                  ? segmentEmptySlash(slashStroke)
                  : undefined,
                boxShadow: tabShadow(activeId === s.id, s.color)
              }}
            />
          );
        }
        return (
        <span
          key={s.id}
          role="button"
          onClick={() => onSelect(s.id)}
          title={s.empty ? `Nothing will be sent to "${s.label}"` : undefined}
          style={{
            ...base,
            background: s.color,
            backgroundImage: s.empty ? segmentEmptySlash(slashStroke) : undefined,
            color: fg,
            boxShadow: tabShadow(activeId === s.id, s.color)
          }}
        >
          {s.label}
          {onDelete && (
            <span
              role="button"
              aria-label="Delete segment"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(s.id);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 14,
                height: 14,
                borderRadius: 4,
                color: fg,
                opacity: 0.75,
                cursor: 'pointer'
              }}
            >
              <X size={11} />
            </span>
          )}
        </span>
      );})}
      {/* Everyone else — the catch-all audience, always LAST, only once segments exist. */}
      {everyone && hasSegments && (condensed ? (
        <span
          role="button"
          onClick={() => onSelect(everyone.id)}
          title={
            everyone.empty
              ? `${everyoneLabel} — nothing will be sent to this segment`
              : `${everyoneLabel} — everyone who doesn't match a segment`
          }
          style={{
            ...miniBase,
            background: color.black,
            backgroundImage: everyone.empty
              ? segmentEmptySlash('rgba(255,255,255,.9)')
              : undefined,
            boxShadow: tabShadow(activeId === everyone.id, color.black)
          }}
        />
      ) : (
        <span
          role="button"
          onClick={() => onSelect(everyone.id)}
          title={
            everyone.empty
              ? `Nothing will be sent to "${everyoneLabel}"`
              : "Everyone who doesn't match a segment"
          }
          style={{
            ...base,
            background: color.black,
            backgroundImage: everyone.empty
              ? segmentEmptySlash('rgba(255,255,255,.9)')
              : undefined,
            color: textOn(color.black),
            boxShadow: tabShadow(activeId === everyone.id, color.black)
          }}
        >
          {everyoneLabel}
        </span>
      ))}
      {onAdd && (condensed ? (
        <button
          type="button"
          onClick={onAdd}
          aria-label="Add segment"
          style={{
            ...miniBase,
            background: color.white,
            color: color.n500,
            border: `1.5px dashed ${color.n300}`,
            boxShadow: 'none'
          }}
        >
          <Plus size={14} />
        </button>
      ) : (
        <button
          type="button"
          onClick={onAdd}
          style={{
            ...base,
            background: color.white,
            color: color.n500,
            border: `1.5px dashed ${color.n300}`,
            boxShadow: 'none',
            fontWeight: font.weight.label
          }}
        >
          + Add segment
        </button>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------- merge-tag pill */

export const MergeTagPill = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${font.size.body}px;
  font-weight: ${font.weight.label};
  padding: 2px ${tokens.space(2)};
  border-radius: ${radius.sm}px;
  background: ${color.blue100};
  color: ${color.blue700};
  & svg {
    width: 14px;
    height: 14px;
  }
`;

/**
 * Assignee/recipient pill in the top bar — same blue as the merge-tag pill but fully
 * rounded (pill) to match the Active status pill's shape.
 */
export const AssigneePill = styled(MergeTagPill)`
  border-radius: ${radius.pill}px;
  padding: 2px ${tokens.space(2.5)};
`;
